"use client";

import { Link, usePathname } from "@/i18n/navigation";
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	navigationMenuTriggerStyle
} from "@/components/ui/navigation-menu";
import { CustomButton } from "@/components/ui/custom-button";
import { useAuth, useOrganization, useOrganizationList, use_lti_mode } from "@/auth/hooks/clerk-hooks";
import { MobileNav } from "./MobileNav";
import { NavbarUserDropdown } from "./NavbarUserDropdown";
import { AdminButton } from "./AdminButton";
import { AuthNavbar } from "./AuthNavbar";
import { MyFiles } from "./MyFiles";
import { marketing_nav_sections, type MarketingNavLink } from "./marketing-nav";

const nav_menu_link_class_name =
	"group relative block overflow-hidden rounded-lg px-3 py-2 leading-none no-underline outline-hidden transition-[background-color,transform,padding-left] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:origin-top before:scale-y-0 before:rounded-full before:bg-ss-blue before:transition-transform before:duration-300 before:ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-0.5 hover:bg-slate-50 hover:pl-4 hover:before:scale-y-100 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2";

function MarketingMenuLink({ item }: { item: MarketingNavLink }) {
	const content = (
		<>
			<div>
				<h6 className="text-sm font-semibold text-slate-950 transition-colors duration-200 group-hover:text-ss-blue">{item.title}</h6>
			{item.description && (
				<p className="mt-1 line-clamp-3 text-xs leading-relaxed text-slate-500">
					{item.description}
				</p>
			)}
			</div>
		</>
	);

	if (item.external) {
		return (
			<NavigationMenuLink asChild>
				<a
					href={item.href}
					target="_blank"
					rel="noopener noreferrer"
					className={nav_menu_link_class_name}
				>
					{content}
				</a>
			</NavigationMenuLink>
		);
	}

	return (
		<NavigationMenuLink asChild>
			<Link href={item.href} className={nav_menu_link_class_name}>
				{content}
			</Link>
		</NavigationMenuLink>
	);
}

const Navbar = () => {
	const pathname = usePathname();
	const { isSignedIn } = useAuth();
	const { organization } = useOrganization();
	const { isLoaded: orgs_loaded, userMemberships } = useOrganizationList({
		userMemberships: true
	});
	const is_lti_mode = use_lti_mode();

	const current_membership = organization && orgs_loaded
		? userMemberships.data?.find((membership) => membership.organization.id === organization.id)
		: null;
	const user_role = current_membership?.role;
	const role_display_map: Record<string, string> = {
		"org:admin": "Admin",
		"org:superadmin": "Superadmin",
		"org:professor": "Professor"
	};
	const display_role = user_role && user_role !== "org:student"
		? role_display_map[user_role] || user_role
		: null;
	const show_authenticated_nav = isSignedIn || is_lti_mode;

	if (
		pathname.includes("/book-a-demo") ||
		pathname.includes("/admin") ||
		pathname.includes("/superadmin")
	) {
		return <></>;
	}

	const is_auth_workspace_route = pathname.startsWith("/app") || pathname.startsWith("/study");

	if (show_authenticated_nav && is_auth_workspace_route) {
		return (
			<AuthNavbar
				pathname={pathname}
				is_signed_in={isSignedIn}
				is_lti_mode={is_lti_mode}
				user_role={user_role}
				display_role={display_role}
			/>
		);
	}

	const logo_href = show_authenticated_nav ? "/app" : "/";
	const logo = (
		<div className="z-40 flex flex-row items-center justify-start custom-hover-div">
			<img
				src="/StudyStash - Logo_Colour - RGB.png"
				alt="StudyStash Brand Mark"
				width={180}
				className={`h-auto cursor-pointer object-contain pt-1 ${
					isSignedIn ? "w-[80px] xs:w-[94px] sm:w-[121px]" : "w-[120px] xs:w-[140px] sm:w-[180px]"
				}`}
			/>
		</div>
	);

	return (
		<nav
			aria-label="Main navigation"
			className="left-0 right-0 top-0 z-60 bg-white px-2 transition-opacity duration-200 sm:px-4 md:px-[10px]"
		>
			<div className="z-40 mx-auto flex h-[60px] flex-col justify-center py-1">
				<div className="relative flex flex-row items-center justify-between px-4 lg:pr-1">
					<div className="flex shrink-0 items-center gap-2">
						<Link href={logo_href}>{logo}</Link>
					</div>

					<div className="flex min-w-0 shrink-0 items-center gap-1 lg:gap-4">
						{!show_authenticated_nav && (
							<div className="flex items-center">
								<NavigationMenu className="hidden md:flex md:flex-row">
									<NavigationMenuList className="flex gap-1 md:gap-2 lg:gap-3">
										{marketing_nav_sections.map((section) => (
											<NavigationMenuItem key={section.title} className="list-none">
												{section.items?.length ? (
													<>
														<NavigationMenuTrigger className="px-2 lg:px-3">
															{section.title}
														</NavigationMenuTrigger>
														<NavigationMenuContent>
															<div className="z-40 grid w-[560px] grid-cols-2 gap-1 rounded-xl bg-white p-3 shadow-[0_18px_50px_rgba(15,23,42,0.11)] ring-1 ring-slate-200/80">
																{section.items.map((item) => (
																	<MarketingMenuLink key={item.href} item={item} />
																))}
															</div>
														</NavigationMenuContent>
													</>
												) : (
													<NavigationMenuLink asChild>
														<Link href={section.href} className={navigationMenuTriggerStyle()}>
															{section.title}
														</Link>
													</NavigationMenuLink>
												)}
											</NavigationMenuItem>
										))}
									</NavigationMenuList>
								</NavigationMenu>

								<div className="pr-2 md:pr-0">
									<MobileNav />
								</div>

								<div className="ml-0 mr-0 hidden h-10 w-px bg-green-400 sm:ml-4 md:ml-6 lg:ml-8 lg:mr-4 lg:block" />

								<Link href="/register" className={`${navigationMenuTriggerStyle()} hidden sm:inline-flex`}>
									Sign up
								</Link>

								<Link href="/login">
									<CustomButton
										variant="primary"
										size="lg"
										className="px-3 py-1.5 text-xs sm:px-5 sm:py-2 sm:text-sm md:px-8 md:py-3 md:text-base"
									>
										Login
									</CustomButton>
								</Link>
							</div>
						)}

						<nav
							aria-label="User account navigation"
							className={`relative bottom-[2px] mt-2 flex flex-row flex-wrap items-center justify-end gap-4 sm:flex-nowrap ${
								isSignedIn ? "justify-end" : "justify-center sm:justify-end"
							}`}
						>
							<div className="flex justify-end gap-4">
								{show_authenticated_nav && (
									<>
										<div className="hidden sm:flex">{!pathname?.startsWith("/app") && <MyFiles />}</div>
										<div className="hidden sm:flex">{["org:admin", "org:superadmin"].includes(user_role || "") && <AdminButton />}</div>

										{isSignedIn && (
											<div className="flex flex-row items-center gap-4">
												<NavbarUserDropdown is_lti_mode={is_lti_mode} />
											</div>
										)}
									</>
								)}
							</div>
						</nav>
					</div>
				</div>
			</div>
		</nav>
	);
};

export default Navbar;
